/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'antd-v5';
import styled from 'styled-components';
import { ROUTER_ACCOUNT, ROUTER_BACKOFFICE, ROUTER_MY_STORE_BUNDLES, ROUTER_SETTINGS_GENERAL } from 'app/AppRouter';
import {
  ChevronRightIcon,
  DesktopIcon,
  LockIcon,
  MyStoreIcon,
  SettingLineIcon,
  SortRandomIcon,
  UserCircleIcon,
} from 'assets/icons';
import ConnectAs from 'components/Page/Header/TopHeader/Profile/ConnectAs';
import { HEADER_HEIGHT } from 'components/Page/Header/TopHeader/TopHeader';
import { getAvatar } from 'components/ProfileInfo/ProfileInfo';
import PrivateComponent from 'composers/PrivateComponent';
import { getAdminPortalLink, getMyStoreEnabled, getUserDelegationEnabled } from 'domains/appContext/AppContext.store';
import {
  disconnectDelegationSessionRequest,
  getActiveSession,
  getActiveSessionStatus,
  getIsDisconnecting,
} from 'domains/delegation';
import { getTokenUser, getUserRights, UserRole } from 'domains/user';
import { getBackOfficeMenuItems } from 'pages/BackOfficePage/BackOfficeLayout/BackOfficeMenu';
import { theme } from 'styles';
import { Flex, Icon, IconColorMode, IconType, Image, Link, MarginBox, Spin, StatusDot, Text } from 'UI';
import { LOADING } from 'utils';
import { ProfilePic } from './ProfileCard.styled';
import { ExitConnectAsWrapper, MenuItemDivider, SMenuItem } from '../TopHeader.styled';

interface ProfileCardProps {
  setProfileVisible: (visible: boolean) => void;
  visible: boolean;
}

interface LinksProps {
  setProfileVisible: (visible: boolean) => void;
  setView: (view: 'profile' | 'connect_as') => void;
}

interface MenuItemProps {
  label: string;
  link: string;
  external: boolean;
  newTab?: boolean;
  icon: IconType;
  iconColorMode?: IconColorMode;
  requiredRights: UserRole[];
  'data-cy'?: string;
  onSelect?: () => void;
}

export interface ProfileCardItemProps extends MenuItemProps {
  setProfileVisible: (visible: boolean) => void;
}

interface ConnectAsDisconnectProps {
  setProfileVisible: (visible: boolean) => void;
  setView: (view: 'profile' | 'connect_as') => void;
}

const ProfileCardWrapper = styled(Flex)`
  overflow: scroll;
`;

const ProfileCard = ({ setProfileVisible, visible }: ProfileCardProps) => {
  const [view, setView] = useState<'profile' | 'connect_as'>('profile');
  const activeSession = useSelector(getActiveSession);

  useEffect(() => {
    if (!visible) {
      setView('profile');
    }
  }, [visible]);

  const renderContent = () => {
    switch (view) {
      case 'connect_as':
        return <ConnectAs onBack={() => setView('profile')} />;
      case 'profile':
      default:
        return (
          <>
            {activeSession != null && <ConnectAsDisconnect setProfileVisible={setProfileVisible} setView={setView} />}
            <MarginBox mt={50} />
            <UserSection />
            <MarginBox mt={20} />
            <LinkSection setProfileVisible={setProfileVisible} setView={setView} />
          </>
        );
    }
  };

  return (
    <ProfileCardWrapper
      direction={'column'}
      minWidth={520}
      maxWidth={520}
      maxHeight={window.innerHeight - HEADER_HEIGHT}
      data-cy={'user-info-popover'}
      minHeight={500}
    >
      {renderContent()}
    </ProfileCardWrapper>
  );
};

const UserSection = () => {
  const userInfo = useSelector(getTokenUser);
  const activeSession = useSelector(getActiveSession);
  const sessionStatus = useSelector(getActiveSessionStatus);

  const { t } = useTranslation();

  return (
    <Flex direction={'column'} align={'center'} data-cy={'connected-as-section'}>
      <ProfilePic>
        <Image alt={'avatar'} src={getAvatar(userInfo?.id)} width={80} height={80} />
      </ProfilePic>
      {sessionStatus === LOADING && <Spin size={'small'} />}
      <Flex align={'center'} gap={8} data-cy={'connected-as-info'}>
        {activeSession ? (
          <Icon
            IconComponent={SortRandomIcon}
            size={18}
            noPointer
            color={theme.color.status_error_text_active}
            colorMode={'currentColor'}
          />
        ) : (
          <StatusDot color={'selected'} />
        )}
        <Text type="sm" color="grey55">
          {t('header.connected_as.garage', 'Connected as: {{garageName}}', {
            garageName: activeSession
              ? activeSession.delegatedBuyerName ?? ''
              : `${userInfo?.firstName} ${userInfo?.lastName}`,
          })}
        </Text>
      </Flex>
    </Flex>
  );
};

const DefaultMenuItemRenderer = (menuItem: ProfileCardItemProps) => {
  const handleClick = () => {
    if (menuItem.onSelect) {
      menuItem.onSelect();
    } else {
      menuItem.setProfileVisible(false);
    }
  };

  const itemContent = (
    <SMenuItem minHeight={56} align={'center'} justify={'space-between'} data-cy={menuItem['data-cy']}>
      <Flex align={'center'} gap={16}>
        <Icon IconComponent={menuItem.icon} size={24} display={'inline'} colorMode={menuItem.iconColorMode} />
        <Text type="base" cursor={'pointer'}>
          {menuItem.label}
        </Text>
      </Flex>
      <Icon IconComponent={ChevronRightIcon} size={14} display={'inline'} color={theme.color.grey55} />
    </SMenuItem>
  );

  return (
    <PrivateComponent
      key={menuItem.label}
      render={() => (
        <MenuItemDivider onClick={handleClick}>
          {menuItem.onSelect ? (
            itemContent
          ) : (
            <Link to={menuItem.link} external={menuItem.external} newTab={menuItem.newTab}>
              {itemContent}
            </Link>
          )}
        </MenuItemDivider>
      )}
      requiredRights={menuItem.requiredRights ?? []}
    />
  );
};

const ConnectAsDisconnect = ({ setProfileVisible, setView }: ConnectAsDisconnectProps) => {
  const dispatch = useDispatch();
  const isDisconnecting = useSelector(getIsDisconnecting);
  const { t } = useTranslation();

  const handleDisconnectFrom = () => {
    dispatch(disconnectDelegationSessionRequest());
    setProfileVisible(false);
  };

  return (
    <PrivateComponent
      render={() => (
        <Dropdown
          placement={'bottomLeft'}
          trigger={['click']}
          menu={{
            items: [
              {
                label: t('common.action.exit_impersonation.action.connect_as_another', 'Connected as another garage'),
                key: 'connect_as_another',
                onClick: () => setView('connect_as'),
              },
              {
                label: t('common.action.exit_impersonation.action.exit', 'Go back to my Profile'),
                key: 'exit_impersonation',
                onClick: handleDisconnectFrom,
                disabled: isDisconnecting,
              },
            ],
          }}
        >
          <ExitConnectAsWrapper>
            <Text type="base" cursor={'pointer'} color={'status_error_text_active'}>
              {t('common.action.exit_impersonation.title', 'Exit impersonation')}
            </Text>
          </ExitConnectAsWrapper>
        </Dropdown>
      )}
      requiredRights={[UserRole.CONNECT, UserRole.CENTRAL_ADMIN]}
    />
  );
};

const LinkSection = ({ setProfileVisible, setView }: LinksProps) => {
  const { t } = useTranslation();
  const rights = useSelector(getUserRights);
  const userDelegationEnabled = useSelector(getUserDelegationEnabled);
  const userRights = useSelector(getUserRights);
  const myStoreEnabled = useSelector(getMyStoreEnabled);
  const adminPortalUrl = useSelector(getAdminPortalLink);
  const connectAsEnabled =
    userDelegationEnabled && (rights.includes(UserRole.CONNECT) || rights.includes(UserRole.CENTRAL_ADMIN));

  return (
    <Flex direction={'column'}>
      <DefaultMenuItemRenderer
        setProfileVisible={setProfileVisible}
        link={ROUTER_ACCOUNT}
        external={false}
        icon={UserCircleIcon}
        requiredRights={[]}
        label={t('common.user_profile', 'User profile')}
        data-cy={'user-profile-link'}
      />
      {connectAsEnabled && (
        <DefaultMenuItemRenderer
          setProfileVisible={setProfileVisible}
          link={''}
          external={false}
          icon={SortRandomIcon}
          iconColorMode={'currentColor'}
          requiredRights={[UserRole.CONNECT, UserRole.CENTRAL_ADMIN]}
          label={t('connect_as', 'Connect as')}
          data-cy={'connect-as-link'}
          onSelect={() => setView('connect_as')}
        />
      )}
      {myStoreEnabled && (
        <DefaultMenuItemRenderer
          setProfileVisible={setProfileVisible}
          link={ROUTER_MY_STORE_BUNDLES}
          external={false}
          icon={MyStoreIcon}
          requiredRights={[UserRole.R2, UserRole.R3, UserRole.R3_MOTRIO]}
          label={t('my-store', 'My store')}
          data-cy={'my-store-link'}
        />
      )}
      {adminPortalUrl && (
        <DefaultMenuItemRenderer
          setProfileVisible={setProfileVisible}
          link={adminPortalUrl}
          external
          newTab
          icon={LockIcon}
          requiredRights={[UserRole.R1]}
          label={t('admin_portal', 'Admin portal')}
          data-cy={'admin-portal-link'}
        />
      )}
      <DefaultMenuItemRenderer
        setProfileVisible={setProfileVisible}
        link={`${ROUTER_BACKOFFICE}/${getBackOfficeMenuItems(userRights, t)[0]?.link}`}
        external={false}
        icon={DesktopIcon}
        requiredRights={[UserRole.CENTRAL_ADMIN, UserRole.COUNTRY_ADMIN, UserRole.R1]}
        label={t('backoffice', 'Back office')}
        data-cy={'backoffice-link'}
      />
      <DefaultMenuItemRenderer
        setProfileVisible={setProfileVisible}
        link={ROUTER_SETTINGS_GENERAL}
        external={false}
        icon={SettingLineIcon}
        requiredRights={[]}
        label={t('common.settings', 'Settings')}
        data-cy={'settings-link'}
      />
    </Flex>
  );
};

export default ProfileCard;
